/**
 * Kisscurls - Similar Sets Product Recommendations
 * Strictly ranks by:
 * 1. Exact color + same length group
 * 2. Exact color + neighbor length groups
 * 3. Neighbor color(s) in order of proximity -> same length group, then neighbor length groups
 * Inside each tier: sorted by absolute length difference (cm), stable tie-break by product ID.
 */

if (!customElements.get('similar-sets-recommendations')) {
  class SimilarSetsRecommendations extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.observer = null;
      this.rules = null;
      this.pubSubUnsubscriber = null;
      this.onVariantChange = null;
      this.onResize = null;
      this.resizeTimeout = null;
      this.targetSection = null;
      this.originalParent = null;
      this.originalNextSibling = null;
      this.isRelocating = false;
      this.isLoaded = false;
      this.isLoading = false;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.isRelocating) return;

      this.cacheOriginalPlacement();
      this.initRules();

      if (!this.initialized) {
        this.initialized = true;
        this.setupResizeListener();
        this.setupVariantListener();
        this.setupLifecycleRelocation();
        this.setupObserver();
      }

      this.repositionSection();
    }

    disconnectedCallback() {
      if (this.isRelocating) return;

      // Clean up only when actually detached from the document permanently
      if (!this.isConnected) {
        this.observer?.disconnect();
        this.observer = null;
        this.abortController?.abort();
        if (this.onResize && typeof window !== 'undefined') {
          window.removeEventListener('resize', this.onResize);
          this.onResize = null;
        }
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = null;
        }
        if (this.onVariantChange && typeof document !== 'undefined') {
          document.removeEventListener('variant:change', this.onVariantChange);
          window.removeEventListener('popstate', this.onVariantChange);
          this.onVariantChange = null;
        }
        if (typeof this.pubSubUnsubscriber === 'function') {
          this.pubSubUnsubscriber();
          this.pubSubUnsubscriber = null;
        }
        this.initialized = false;
      }
    }

    cacheOriginalPlacement() {
      if (!this.originalParent) {
        this.targetSection = (typeof this.closest === 'function' ? this.closest('.shopify-section') : null) || this;
        this.originalParent = this.targetSection?.parentElement || null;
        this.originalNextSibling = this.targetSection?.nextSibling || null;
      }
    }

    restoreOriginalPlacement() {
      this.cacheOriginalPlacement();
      const targetSection = this.targetSection;
      if (!targetSection || !this.originalParent) return;

      targetSection.classList?.remove?.(
        'similar-sets-section--under-buy-buttons',
        'similar-sets-section--under-main-product'
      );

      if (
        targetSection.parentElement === this.originalParent &&
        targetSection.nextSibling === this.originalNextSibling
      ) {
        return;
      }

      this.isRelocating = true;
      try {
        this.originalParent.insertBefore(targetSection, this.originalNextSibling);
      } finally {
        this.isRelocating = false;
      }
    }

    setupResizeListener() {
      if (typeof window === 'undefined' || this.onResize) return;
      this.onResize = () => {
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.repositionSection();
          const sliderComponent = this.querySelector?.('slider-component');
          if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
            sliderComponent.resetPages();
          }
        }, 150);
      };
      window.addEventListener('resize', this.onResize);
    }

    setupLifecycleRelocation() {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;

      // 1. Next animation frame
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => this.repositionSection());
      }

      // 2. Short staggered timeouts for async DOM hydrations
      [50, 150, 300, 600].forEach((delay) => {
        setTimeout(() => this.repositionSection(), delay);
      });

      // 3. Document ready states
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.repositionSection(), { once: true });
      }
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.repositionSection(), { once: true });
      }

      // 4. Orientation change
      window.addEventListener('orientationchange', () => {
        setTimeout(() => this.repositionSection(), 100);
      });
    }

    isMobileViewport() {
      if (typeof window === 'undefined') return false;
      // Standard Shopify breakpoint
      if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return true;
      // Window innerWidth
      if (typeof window.innerWidth === 'number' && window.innerWidth < 750) return true;
      // Root clientWidth
      if (document.documentElement && typeof document.documentElement.clientWidth === 'number' && document.documentElement.clientWidth < 750) {
        return true;
      }
      // Mobile screen dimensions in portrait
      if (window.screen && typeof window.screen.width === 'number' && window.screen.width < 750) {
        if (!window.matchMedia || window.matchMedia('(orientation: portrait)').matches) {
          return true;
        }
      }
      return false;
    }

    isProductSoldOut() {
      // 1. Explicit data attribute
      if (this.dataset?.productAvailable === 'false') return true;

      // 2. Real DOM submit button inspection
      if (typeof document !== 'undefined') {
        const submitBtn = document.querySelector(
          '[id*="ProductSubmitButton"], .product-form__submit, button[name="add"]'
        );
        if (submitBtn && submitBtn.hasAttribute('disabled')) {
          const text = (submitBtn.textContent || '').trim().toLowerCase();
          if (
            text.includes('wyprzedan') ||
            text.includes('sold') ||
            text.includes('niedostęp') ||
            text.includes('unavailable')
          ) {
            return true;
          }
        }
      }

      return false;
    }

    findBuyButtonsBlock() {
      if (typeof document === 'undefined') return null;
      const selectors = [
        '.product-form',
        'product-form',
        '[id*="ProductSubmitButton"]',
        '.product-form__buttons',
        'button[name="add"]'
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) {
          // Direct block child of .product__info-container
          const containerChild = el.closest('.product__info-container > *');
          if (containerChild) return containerChild;
          const container = el.closest('.product__info-container');
          if (container && el.parentElement === container) return el;
          if (el.parentElement && el.parentElement !== document.body) return el.parentElement;
          return el;
        }
      }
      return null;
    }

    repositionSection() {
      if (typeof document === 'undefined' || typeof window === 'undefined') return;
      this.cacheOriginalPlacement();
      const targetSection = this.targetSection;
      if (!targetSection || !this.originalParent) return;

      const isSoldOut = this.isProductSoldOut();

      if (!isSoldOut) {
        // In-stock products stay in original position
        this.restoreOriginalPlacement();
        return;
      }

      // Sold out product: move higher up
      const isMobile = this.isMobileViewport();

      if (isMobile) {
        // Mobile (< 750px): Place immediately under the sold out button & notice
        const buyButtonsBlock = this.findBuyButtonsBlock();

        if (buyButtonsBlock && buyButtonsBlock.parentElement) {
          targetSection.classList?.remove?.('similar-sets-section--under-main-product');
          targetSection.classList?.add?.('similar-sets-section--under-buy-buttons');
          if (buyButtonsBlock.nextElementSibling === targetSection) {
            return; // Already in place
          }
          this.isRelocating = true;
          try {
            buyButtonsBlock.insertAdjacentElement('afterend', targetSection);
          } finally {
            this.isRelocating = false;
          }
          return;
        } else {
          // Mobile anchor not yet ready, retry shortly rather than eagerly falling back to desktop
          setTimeout(() => this.repositionSection(), 60);
          return;
        }
      }

      // Desktop (>= 750px): Place immediately under the main product card section
      const mainSection =
        document.querySelector('.section-main-product, [id*="__main"]')?.closest?.('.shopify-section') ||
        document.querySelector('[id*="__main"]');

      if (mainSection && mainSection.parentElement) {
        targetSection.classList?.remove?.('similar-sets-section--under-buy-buttons');
        targetSection.classList?.add?.('similar-sets-section--under-main-product');
        if (mainSection.nextElementSibling === targetSection) {
          return; // Already in place
        }
        this.isRelocating = true;
        try {
          mainSection.insertAdjacentElement('afterend', targetSection);
        } finally {
          this.isRelocating = false;
        }
        return;
      }

      // Fallback: restore original position if target anchors not found
      this.restoreOriginalPlacement();
    }

    initRules() {
      const sectionId = this.dataset.sectionId;
      const rulesEl = typeof document !== 'undefined' ? document.getElementById(`SimilarSetsRules-${sectionId}`) : null;
      if (rulesEl) {
        try {
          this.rules = JSON.parse(rulesEl.textContent);
        } catch (e) {
          console.error('Failed to parse SimilarSetsRules JSON:', e);
          this.rules = { colors: {}, length_groups: [] };
        }
      } else if (!this.rules) {
        this.rules = { colors: {}, length_groups: [] };
      }
    }

    setupObserver() {
      if (typeof IntersectionObserver === 'undefined') return;
      if (this.isLoaded || this.isLoading) return;
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    setupVariantListener() {
      if (typeof document === 'undefined' || this.onVariantChange) return;
      this.onVariantChange = (event) => {
        const variant = event?.data?.variant;
        let shouldReload = false;

        if (variant) {
          const newAvail = variant.available ? 'true' : 'false';
          if (newAvail !== this.dataset.productAvailable) {
            this.dataset.productAvailable = newAvail;
            this.repositionSection();
          }
        }

        const activeInput = document.querySelector('variant-radios input:checked, variant-selects select');
        if (activeInput) {
          const variantColor = activeInput.dataset?.colorId;
          if (variantColor && variantColor !== this.dataset.colorIds) {
            this.dataset.colorIds = variantColor;
            shouldReload = true;
          }
        }

        if (shouldReload) {
          this.isLoaded = false;
          this.loadRecommendations();
        }
      };
      document.addEventListener('variant:change', this.onVariantChange);
      window.addEventListener('popstate', this.onVariantChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.variantChange) {
        this.pubSubUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }
    }

    async loadRecommendations() {
      if (this.isLoading) return;
      this.isLoading = true;

      const baseProductId = String(this.dataset?.productId || '').trim();
      const baseColorIds = (this.dataset?.colorIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const baseLength = parseInt(this.dataset?.length, 10);
      const sectionId = this.dataset?.sectionId || 'similar-sets';
      const collectionUrl = this.dataset?.collectionUrl || '/collections/all';
      const limit = parseInt(this.dataset?.limit, 10) || 4;

      const gridList = this.querySelector?.('.similar-sets-grid');
      const emptyState = this.querySelector?.('.similar-sets-empty');
      const errorState = this.querySelector?.('.similar-sets-error');
      const headingEl = this.querySelector?.('.similar-sets-heading');
      const sliderButtons = this.querySelector?.('.similar-sets-slider-buttons');

      // Hide previous error state
      if (errorState) errorState.hidden = true;

      // If product has no color defined, immediately show empty state
      if (!baseColorIds.length) {
        if (gridList) gridList.innerHTML = '';
        if (emptyState) emptyState.hidden = false;
        if (headingEl) headingEl.hidden = false;
        if (sliderButtons) sliderButtons.hidden = true;
        this.classList?.add?.('similar-sets-recommendations--loaded');
        this.isLoaded = true;
        this.isLoading = false;
        this.repositionSection();
        return;
      }

      // Collect base colors + allowed neighbor colors
      const queryColorIds = [...baseColorIds];
      for (const bColor of baseColorIds) {
        const neighbors = this.rules?.colors?.[bColor]?.neighbors || [];
        for (const n of neighbors) {
          const nId = typeof n === 'string' ? n : n.id;
          if (nId && !queryColorIds.includes(nId)) {
            queryColorIds.push(nId);
          }
        }
      }

      // Build initial fetch URL
      const initialUrl = new URL(collectionUrl, window.location.origin);
      initialUrl.searchParams.set('filter.v.availability', '1');
      for (const cid of queryColorIds) {
        initialUrl.searchParams.append('filter.v.t.shopify.color-pattern', cid);
      }
      initialUrl.searchParams.set('section_id', sectionId);

      this.abortController?.abort();
      this.abortController = new AbortController();

      let timeoutId = null;
      if (typeof setTimeout !== 'undefined') {
        timeoutId = setTimeout(() => {
          try {
            this.abortController?.abort(new DOMException('Timeout', 'TimeoutError'));
          } catch (_) {}
        }, 10000); // 10s network timeout
      }

      try {
        const allCandidateEls = [];
        let currentUrl = initialUrl.toString();
        let pageCount = 0;
        const MAX_PAGES = 3;
        const visitedUrls = new Set();

        while (currentUrl && pageCount < MAX_PAGES && !visitedUrls.has(currentUrl)) {
          visitedUrls.add(currentUrl);
          pageCount++;

          const response = await fetch(currentUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`HTTP error ${response.status}`);
          const htmlText = await response.text();

          const doc = new DOMParser().parseFromString(htmlText, 'text/html');
          const candidateEls = Array.from(doc.querySelectorAll?.('.similar-sets-candidate') || []);
          allCandidateEls.push(...candidateEls);

          // Find next page URL from DOM or raw markup
          let nextUrl = null;
          if (typeof doc.querySelector === 'function') {
            const nextEl = doc.querySelector('[data-next-url], next-page, .pagination__next, [rel="next"]');
            if (nextEl) {
              nextUrl = nextEl.getAttribute('data-next-url') || nextEl.getAttribute('url') || nextEl.getAttribute('href');
              if (!nextUrl && nextEl.hasAttribute('page')) {
                const nextPageNum = nextEl.getAttribute('page');
                const u = new URL(currentUrl);
                u.searchParams.set('page', nextPageNum);
                nextUrl = u.toString();
              }
            }
          }

          if (!nextUrl && typeof htmlText === 'string') {
            const nextMatch = htmlText.match(/data-next-url=["']([^"']+)["']/i) ||
                              htmlText.match(/<next-page[^>]*url=["']([^"']+)["']/i);
            if (nextMatch) {
              nextUrl = nextMatch[1];
            } else {
              const pageMatch = htmlText.match(/<next-page[^>]*page=["'](\d+)["']/i);
              if (pageMatch) {
                const u = new URL(currentUrl);
                u.searchParams.set('page', pageMatch[1]);
                nextUrl = u.toString();
              }
            }
          }

          if (nextUrl) {
            const parsedNext = new URL(nextUrl, window.location.origin);
            parsedNext.searchParams.set('section_id', sectionId);
            currentUrl = parsedNext.toString();
          } else {
            currentUrl = null;
          }
        }

        const evaluatedCandidates = [];

        const getMatchingGroups = (len) => {
          const groups = this.rules?.length_groups || [];
          return groups.filter((g) => len >= g.min && len <= g.max);
        };

        for (const el of allCandidateEls) {
          const candId = String(el.dataset?.productId || '').trim();
          if (!candId || candId === baseProductId) continue; // Exclude current product

          const isAvailable = el.dataset?.available === 'true';
          if (!isAvailable) continue; // Real stock required

          const candColorIds = (el.dataset?.colorIds || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          const candLength = parseInt(el.dataset?.length, 10);

          // 1. Color matching
          const isExactColor = candColorIds.some((c) => baseColorIds.includes(c));
          let colorTier = -1;

          if (isExactColor) {
            colorTier = 0;
          } else {
            let bestNeighborIndex = Infinity;
            for (const bColor of baseColorIds) {
              const neighbors = this.rules?.colors?.[bColor]?.neighbors || [];
              for (let i = 0; i < neighbors.length; i++) {
                const nId = typeof neighbors[i] === 'string' ? neighbors[i] : neighbors[i].id;
                if (candColorIds.includes(nId) && i < bestNeighborIndex) {
                  bestNeighborIndex = i;
                }
              }
            }
            if (bestNeighborIndex !== Infinity) {
              colorTier = 1 + bestNeighborIndex;
            }
          }

          if (colorTier === -1) continue; // Disqualified color

          // 2. Length matching
          let lengthTier = 0;
          let lengthDistance = 0;

          if (!isNaN(baseLength) && baseLength > 0) {
            if (isNaN(candLength) || candLength <= 0) {
              // Disqualified: base has length, candidate missing length
              continue;
            }

            const baseGroups = getMatchingGroups(baseLength);
            const candGroups = getMatchingGroups(candLength);

            if (baseGroups.length > 0) {
              if (candGroups.length === 0) {
                // Candidate length not in any defined group
                continue;
              }

              // Check if they share any group (same group -> Tier 0)
              const hasSharedGroup = baseGroups.some((bg) =>
                candGroups.some((cg) => cg.id === bg.id)
              );

              if (hasSharedGroup) {
                lengthTier = 0;
              } else {
                // Check if any candGroup is an immediate neighbor of any baseGroup (Level 1 neighbor -> Tier 1)
                const isNeighbor = baseGroups.some((bg) =>
                  candGroups.some((cg) => (bg.neighbors || []).includes(cg.id))
                );

                if (isNeighbor) {
                  lengthTier = 1;
                } else {
                  // Far length group: disqualified
                  continue;
                }
              }
            } else {
              // Base length not in any defined group: accept candidate with valid color
              lengthTier = 0;
            }

            lengthDistance = Math.abs(candLength - baseLength);
          }

          evaluatedCandidates.push({
            id: candId,
            element: el,
            colorTier,
            lengthTier,
            lengthDistance,
          });
        }

        // 3. Strict sort
        // Priority 1: Color tier (exact color=0, neighbor 1=1, neighbor 2=2...)
        // Priority 2: Length tier (same length group=0, neighbor length groups=1)
        // Priority 3: Absolute difference in cm
        // Priority 4: Stable tie-break by product ID
        evaluatedCandidates.sort((a, b) => {
          if (a.colorTier !== b.colorTier) return a.colorTier - b.colorTier;
          if (a.lengthTier !== b.lengthTier) return a.lengthTier - b.lengthTier;
          if (a.lengthDistance !== b.lengthDistance) return a.lengthDistance - b.lengthDistance;
          return String(a.id).localeCompare(String(b.id));
        });

        // 4. Deduplicate
        const seenIds = new Set();
        const uniqueRanked = [];
        for (const item of evaluatedCandidates) {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            uniqueRanked.push(item);
          }
        }

        const topPicks = uniqueRanked.slice(0, limit);

        if (topPicks.length === 0) {
          if (gridList) gridList.innerHTML = '';
          if (emptyState) emptyState.hidden = false;
          if (headingEl) headingEl.hidden = false;
          if (sliderButtons) sliderButtons.hidden = true;
        } else {
          if (emptyState) emptyState.hidden = true;
          if (headingEl) headingEl.hidden = false;
          if (gridList) {
            gridList.innerHTML = '';
            topPicks.forEach((pick, index) => {
              pick.element.classList?.remove?.('similar-sets-candidate');
              pick.element.classList?.add?.('slider__slide');
              if (pick.element.setAttribute) {
                pick.element.setAttribute('id', `Slide-${sectionId}-${index + 1}`);
              } else {
                pick.element.id = `Slide-${sectionId}-${index + 1}`;
              }
              gridList.appendChild(pick.element);
            });
          }

          // Show slider buttons and counter when more than 1 item
          if (sliderButtons) {
            if (sliderButtons.dataset) {
              sliderButtons.dataset.total = String(topPicks.length);
            } else if (sliderButtons.setAttribute) {
              sliderButtons.setAttribute('data-total', String(topPicks.length));
            }
            if (topPicks.length > 1) {
              sliderButtons.hidden = false;
              const totalEl = sliderButtons.querySelector?.('.slider-counter--total');
              if (totalEl) {
                totalEl.textContent = topPicks.length;
              }
            } else {
              sliderButtons.hidden = true;
            }
          }

          const sliderComponent = this.querySelector?.('slider-component');
          if (sliderComponent) {
            sliderComponent.sliderItems = sliderComponent.querySelectorAll?.('[id^="Slide-"]') || [];

            // Ensure slider counter displays total products count in scroll (e.g. 1 / 10)
            const origInit = sliderComponent.initPages ? sliderComponent.initPages.bind(sliderComponent) : null;
            sliderComponent.initPages = function() {
              if (origInit) origInit();
              this.sliderItemsToShow = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
              if (this.sliderItemsToShow.length < 2) return;
              this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
              this.totalPages = topPicks.length;
              if (this.pageTotalElement) {
                this.pageTotalElement.textContent = this.totalPages;
              }
              this.update();
            };

            const updateCounterRange = function() {
              if (!sliderComponent.slider || !sliderComponent.currentPageElement) return;
              const items = sliderComponent.sliderItemsToShow || [];
              if (!items.length) return;

              const clientWidth = sliderComponent.slider.clientWidth;
              const scrollLeft = sliderComponent.slider.scrollLeft;
              const maxScrollLeft = sliderComponent.slider.scrollWidth - clientWidth;
              const isAtStart = scrollLeft <= 4;
              const isAtEnd = maxScrollLeft > 0 && scrollLeft >= maxScrollLeft - 4;

              let first = 1;
              let last = 1;

              if (isAtStart) {
                first = 1;
                const visible = items.filter((el) => {
                  const right = el.offsetLeft + el.clientWidth;
                  return right <= clientWidth + 4;
                });
                last = Math.max(1, visible.length);
              } else if (isAtEnd) {
                last = items.length;
                const visible = items.filter((el) => {
                  const visibleWidth = el.offsetLeft + el.clientWidth - scrollLeft;
                  return visibleWidth >= el.clientWidth * 0.45;
                });
                first = Math.max(1, items.length - visible.length + 1);
              } else {
                const visibleIndices = items
                  .map((el, idx) => {
                    const left = el.offsetLeft - scrollLeft;
                    const right = left + el.clientWidth;
                    const visibleWidth = Math.min(right, clientWidth) - Math.max(left, 0);
                    const isMainVisible = visibleWidth >= el.clientWidth * 0.45;
                    return { idx: idx + 1, isMainVisible };
                  })
                  .filter((x) => x.isMainVisible)
                  .map((x) => x.idx);

                first = visibleIndices[0] || Math.round(scrollLeft / sliderComponent.sliderItemOffset) + 1;
                last = visibleIndices[visibleIndices.length - 1] || first;
              }

              sliderComponent.currentPageElement.textContent = first === last ? String(first) : `${first}–${last}`;
            };

            const origUpdate = sliderComponent.update ? sliderComponent.update.bind(sliderComponent) : null;
            sliderComponent.update = function() {
              if (origUpdate) origUpdate();

              if (!this.slider) return;

              const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
              const isAtEnd =
                maxScrollLeft > 0 &&
                (this.slider.scrollLeft >= maxScrollLeft - 4 ||
                  (this.nextButton && this.nextButton.hasAttribute('disabled')) ||
                  (typeof this.isSlideVisible === 'function' &&
                    this.sliderItemsToShow &&
                    this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])));
              const isAtStart = this.slider.scrollLeft <= 4;

              if (isAtStart && this.prevButton) {
                this.prevButton.setAttribute('disabled', 'disabled');
              } else if (!isAtStart && this.prevButton) {
                this.prevButton.removeAttribute('disabled');
              }

              if (isAtEnd && this.nextButton) {
                this.nextButton.setAttribute('disabled', 'disabled');
              } else if (!isAtEnd && this.nextButton) {
                const lastItem = this.sliderItemsToShow?.[this.sliderItemsToShow.length - 1];
                if (!lastItem || !this.isSlideVisible(lastItem)) {
                  this.nextButton.removeAttribute('disabled');
                }
              }

              updateCounterRange();
            };

            // Provide tolerance for isSlideVisible so subpixel rounding doesn't prevent detection at the end
            sliderComponent.isSlideVisible = function(element, offset = 0) {
              if (!element || !this.slider) return false;
              const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
              return (
                element.offsetLeft + element.clientWidth <= lastVisibleSlide + 4 &&
                element.offsetLeft >= this.slider.scrollLeft - 4
              );
            };

            if (sliderComponent.slider) {
              sliderComponent.slider.addEventListener(
                'scroll',
                () => {
                  const maxScrollLeft = sliderComponent.slider.scrollWidth - sliderComponent.slider.clientWidth;
                  const isAtEnd = maxScrollLeft > 0 && sliderComponent.slider.scrollLeft >= maxScrollLeft - 4;
                  const isAtStart = sliderComponent.slider.scrollLeft <= 4;

                  if (isAtStart && sliderComponent.prevButton) {
                    sliderComponent.prevButton.setAttribute('disabled', 'disabled');
                  } else if (!isAtStart && sliderComponent.prevButton) {
                    sliderComponent.prevButton.removeAttribute('disabled');
                  }

                  if (isAtEnd && sliderComponent.nextButton) {
                    sliderComponent.nextButton.setAttribute('disabled', 'disabled');
                  } else if (!isAtEnd && sliderComponent.nextButton) {
                    sliderComponent.nextButton.removeAttribute('disabled');
                  }

                  updateCounterRange();
                },
                { passive: true }
              );
            }

            if (typeof sliderComponent.resetPages === 'function') {
              sliderComponent.resetPages();
            } else if (typeof sliderComponent.initPages === 'function') {
              sliderComponent.initPages();
            }
          }
        }

        this.classList?.add?.('similar-sets-recommendations--loaded');
        this.isLoaded = true;
        this.repositionSection();
      } catch (err) {
        const isTimeout = err?.name === 'TimeoutError' || err?.message === 'Timeout' || this.abortController?.signal?.reason?.name === 'TimeoutError';
        if (err.name === 'AbortError' && !isTimeout) return;
        console.error('Failed to load similar sets:', err);
        if (errorState) {
          errorState.hidden = false;
          const retryBtn = errorState.querySelector?.('.similar-sets-retry-btn');
          if (retryBtn) {
            retryBtn.onclick = (e) => {
              e.preventDefault();
              this.isLoaded = false;
              this.loadRecommendations();
            };
          }
        }
      } finally {
        this.isLoading = false;
        if (timeoutId && typeof clearTimeout !== 'undefined') {
          clearTimeout(timeoutId);
        }
      }
    }
  }

  customElements.define('similar-sets-recommendations', SimilarSetsRecommendations);
}
